import { type FC, type PropsWithChildren, useContext, useEffect, useRef, useState } from 'react';
import { type Cache, SWRConfig, type SWRConfiguration, type SWRResponse, useSWRConfig } from 'swr';

import { AuthContext } from '@/providers/Auth';
import { type AuthUser } from '@/providers/Auth/constants';

const CACHE_KEY = 'glade-cache';
const CACHE_USER_KEY = 'glade-cache-user';
const WRITE_INTERVAL = 8_000;
const IDLE_TIMEOUT = 6_000;
const EMPTY_CACHE = '[]';
const MAX_CACHE_ENTRIES = 5_000;

const createLocalStorageProvider = (user: AuthUser | null): (() => Cache) => {
  const prevUserStr = window.localStorage.getItem(CACHE_USER_KEY);
  let initialStore: [string, SWRResponse][] = [];
  try {
    // this intentionally fails to retrieve when user is `null`.
    const raw =
      prevUserStr && user && user?.id === JSON.parse(prevUserStr)
        ? (window.localStorage.getItem(CACHE_KEY) ?? EMPTY_CACHE)
        : EMPTY_CACHE;
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((entry) => Array.isArray(entry) && entry.length === 2)) {
      initialStore = (parsed as [string, SWRResponse][])
        .slice(0, MAX_CACHE_ENTRIES)
        .filter(([_, res]) => !res.isLoading && !res.isValidating);
    }
  } catch {
    initialStore = [];
  }

  // When initializing, we restore the data from `localStorage` into a map.
  const map = new Map(initialStore) as unknown as Cache;

  return () => map;
};

const SwrCacheImpl: FC<PropsWithChildren<{ userId?: AuthUser['id'] }>> = ({ userId, children }) => {
  const { cache, mutate } = useSWRConfig();
  const prevUserIdRef = useRef<AuthUser['id'] | undefined>(undefined);
  useEffect(() => {
    // We write the cache as a JSON array of entries.
    const writeToCache = (): void => {
      window.localStorage.setItem(
        CACHE_KEY,
        JSON.stringify(Array.from((cache as unknown as Map<string, unknown>).entries())),
      );
      window.localStorage.setItem(CACHE_USER_KEY, JSON.stringify(userId));
    };
    // We also write to `localStorage` on an interval.
    const intervalId = window.setInterval(() => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(writeToCache, { timeout: IDLE_TIMEOUT });
      } else {
        writeToCache();
      }
    }, WRITE_INTERVAL);
    // Before unloading the app, we write back all the data into `localStorage`
    // and clear the interval. Clean up the listener on unmount.
    const handleBeforeUnload = (): void => {
      writeToCache();
      clearInterval(intervalId);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cache, userId]);

  // Clear SWR cache and localStorage only when the userId changes (not on initial mount)
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    if (prevUserId === undefined) {
      // Initial mount: just record the current userId
      prevUserIdRef.current = userId;
      return;
    }
    if (prevUserId !== userId) {
      // User has changed: clear SWR cache and reset persisted cache
      mutate(() => true, undefined, { revalidate: false });
      window.localStorage.setItem(CACHE_KEY, EMPTY_CACHE);
      window.localStorage.setItem(CACHE_USER_KEY, JSON.stringify(userId));
      prevUserIdRef.current = userId;
    }
  }, [userId, mutate]);
  return children;
};

export const SwrCache: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [configValue, setConfigValue] = useState<SWRConfiguration | null>(null);
  // This has to be in a `useEffect` to avoid NextJS trying to run it on the server.
  useEffect(() => {
    const provider = createLocalStorageProvider(user);
    setConfigValue({ provider } satisfies SWRConfiguration);
  }, [user]);
  return configValue ? (
    <SWRConfig value={configValue}>
      <SwrCacheImpl userId={user?.id ?? undefined}>{children}</SwrCacheImpl>
    </SWRConfig>
  ) : (
    children
  );
};
